/* R148c · Feb 2026 — Idle logout watcher.
 * Tracks activity events (mousemove/keydown/click/scroll/touchstart).
 * Warns at 20 min of idle, force-logs-out at 30 min.
 * Remembers the last visited page so the next login lands you back there.
 *
 * Timeouts are constants for now (30 / 10 min). Kept as a global mount in
 * AppShell so it only runs while the user is authenticated. */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const DEFAULT_TIMEOUT_MIN = 30;
const DEFAULT_WARN_BEFORE_MIN = 10;
// Throttle activity listeners so we don't reset the timer 100x per second.
const ACTIVITY_THROTTLE_MS = 5000;

export default function IdleLogoutWatcher() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const lastActivity = useRef(Date.now());
  const warnedAt = useRef(0);
  const timerRef = useRef(null);
  const warnToastId = useRef(null);
  const timeoutMin = DEFAULT_TIMEOUT_MIN;
  const warnBeforeMin = DEFAULT_WARN_BEFORE_MIN;

  // Remember the last-visited /app page so we can restore it on next login.
  useEffect(() => {
    if (!user) return;
    if (loc.pathname.startsWith("/app")) {
      try {
        localStorage.setItem("acq_last_page", loc.pathname + (loc.search || ""));
      } catch { /* quota / private mode */ }
    }
  }, [user, loc.pathname, loc.search]);

  useEffect(() => {
    if (!user) return;

    const bump = () => {
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
      lastActivity.current = now;
      warnedAt.current = 0;
      if (warnToastId.current) {
        toast.dismiss(warnToastId.current);
        warnToastId.current = null;
      }
    };

    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const tick = () => {
      const idleMs = Date.now() - lastActivity.current;
      const warnAtMs = (timeoutMin - warnBeforeMin) * 60 * 1000;
      const timeoutMs = timeoutMin * 60 * 1000;
      if (idleMs >= timeoutMs) {
        // Force logout.
        try {
          if (loc.pathname.startsWith("/app")) {
            localStorage.setItem("acq_last_page", loc.pathname + (loc.search || ""));
          }
        } catch { /* ignore */ }
        toast.error("Logged out due to inactivity", { duration: 6000 });
        logout();
      } else if (idleMs >= warnAtMs && warnedAt.current === 0) {
        warnedAt.current = Date.now();
        warnToastId.current = toast.warning(
          `You'll be logged out in ${warnBeforeMin} minutes due to inactivity. Move your mouse or press a key to stay signed in.`,
          { duration: warnBeforeMin * 60 * 1000, id: "idle-warn", important: true },
        );
      }
    };

    timerRef.current = setInterval(tick, 15000); // check every 15s

    return () => {
      evts.forEach((e) => window.removeEventListener(e, bump));
      if (timerRef.current) clearInterval(timerRef.current);
      if (warnToastId.current) toast.dismiss(warnToastId.current);
    };
  }, [user, logout, timeoutMin, warnBeforeMin, loc.pathname, loc.search]);

  return null;
}
